import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { HSAContributionsResponseDto } from '../../queries/useContributionAmountQuery/utils/types';

const initialState: HSAContributionsResponseDto = {
  disable_hsa_form: false,
  show_views_edit_section: false,
  requires_eoi: false,
  irs_max: 0,
  irs_for_family: 0,
  irs_year: 0,
  current_plan_year: 0,
  contribution_match_method: "",
  previous_org_contribution: undefined,
  previous_org_contribution_amount: 0,
  reset_all_employee_changes: false,
  employer_contribution_details: {
    fsa_match_method: '',
    org_max_amount: '',
    org_base_amount: '',
    contribution_frequency_type: '',
    contribution_frequency_amount: '',
    next_contribution_date: '',
    total_er_lump_sum_contribution_amount: '',
    total_org_contribution_amount: '',
    next_deduction_date: ''
  },
  employee_contribution_details: {
    subscriber_fsa_contribution_type: '',
    calculated_subscriber_max: '',
    calculated_subscriber_min: '',
    remaining_paycheck_dates: [],
    employeeBalanceContributionAmount: 0,
    remaining_paydays: 0,
    total_paydays: 0,
    total_ee_lump_sum_contribution_amount: 0,
    total_ee_contribution_amount: 0,
  },
  scheduled_contribution: [
    {
      paycheck: "",
      org_payroll_schedule_id: 0,
      scheduled_employer_contribution: '',
      scheduled_employer_lump_sum_contribution: '',
      scheduled_subscriber_contribution: '',
      scheduled_subscriber_lump_sum_contribution: '',
      is_skip_pay_date: false,
    },
  ],
};

const newHsaContributions = createSlice({
  name: 'HSAContributionAmountCalculations',
  initialState,
  reducers: {
    updateHSAContributionResponse: (state, { payload }: PayloadAction<any>) => {
      return {
        ...state,
        ...payload,
        employee_contribution_details: {
          ...state.employee_contribution_details,
          ...(payload.employee_contribution_details)
        },
        employer_contribution_details: {
          ...state.employer_contribution_details,
          ...(payload.employer_contribution_details)
        },
        previous_contribution_given: payload.previous_org_contribution ? payload.previous_org_contribution : false,
        scheduled_contribution: Array.isArray(payload.scheduled_contribution) ? payload.scheduled_contribution : state.scheduled_contribution
      };
    },
    updateResetAllEmployeeChanges: (state, { payload }: PayloadAction<boolean>) => {
      state.reset_all_employee_changes = payload;
    },
    resetHsaInitalResposne: (state, { payload }: PayloadAction<HSAContributionsResponseDto>) => {
      return {
        ...initialState,
        ...payload
      };
    },
    updateContributeType: (state, { payload }: PayloadAction<string>) => {
      state.employee_contribution_details = { ...state.employee_contribution_details, subscriber_fsa_contribution_type: payload };
    },
    updateBalanceContributionAmount: (state, { payload }: PayloadAction<number>) => {
      state.employee_contribution_details = { ...state.employee_contribution_details, employeeBalanceContributionAmount: payload.toFixed(2) };
    },
    updatePreviousContribution: (state, { payload }: PayloadAction<boolean | null | undefined>) => {
      state.previous_org_contribution = payload;
    },
    updatePreviousContributionAmount: (state, { payload }: PayloadAction<number>) => {
      state.previous_org_contribution_amount = payload;
    },
    updateScheduledContribution: (state, { payload }: PayloadAction<{ scheduled_contribution: any[], disable_hsa_form: boolean }>) => {
      const totalScheduledSubscriberContribution = (payload.scheduled_contribution || []).reduce((total, contribution) => Number(total) + Number(contribution.scheduled_subscriber_contribution), 0);
      const lumpSumContribution = (payload.scheduled_contribution || []).reduce((total, contribution) => Number(total) + Number(contribution.scheduled_subscriber_lump_sum_contribution), 0);

      return {
        ...state,
        scheduled_contribution: payload.scheduled_contribution,
        disable_hsa_form: payload.disable_hsa_form,
        employee_contribution_details: {
          ...state.employee_contribution_details,
          total_ee_contribution_amount: totalScheduledSubscriberContribution.toFixed(2),
          total_ee_lump_sum_contribution_amount: lumpSumContribution.toFixed(2)
        }
      };
    },
    updateContributionDetails: (state, { payload }: PayloadAction<any>) => {
      state.employee_contribution_details.calculated_subscriber_max = String(payload.employeeContribution);
      state.employer_contribution_details.total_org_contribution_amount = String(payload.employerTotalFrequencyContribution);
      state.employer_contribution_details.contribution_frequency_amount = String(payload.employerContributionFrequencyAmount);
      state.employer_contribution_details.total_er_lump_sum_contribution_amount = String(payload.employerLumpSumAmount);
    }
  },
});

const { reducer, actions } = newHsaContributions;

export const { updateHSAContributionResponse, updateContributeType, updateBalanceContributionAmount, updatePreviousContribution, updatePreviousContributionAmount, updateScheduledContribution, updateContributionDetails, updateResetAllEmployeeChanges, resetHsaInitalResposne } = actions;

export const newHsaContributionReducer = reducer;
